import type { ReactNode } from 'react';

interface Props {
  /**
   * Optional brand-flair label shown above the title in primary tracking.
   * Used by branded screens (e.g. HomeScreen workspace hero). Grid screens
   * typically omit it for a more compact header.
   */
  eyebrow?: string;
  title: string;
  /**
   * Substring within `title` to render in italicised display font.
   * Example: title="Welcome back, Sam", titleAccent="Sam" →
   *   Welcome back, <span class="font-display italic text-primary">Sam</span>
   * If absent or not found, the title renders plain. Grid screens omit this.
   */
  titleAccent?: string;
  subtitle?: string;
  /**
   * Inline slot rendered on the right side of the header row, aligned to
   * the bottom of the header zone (level with the subtitle baseline).
   * Use for status tab pills, segmented controls, or other secondary
   * navigation that belongs *next to* the title rather than in the topbar.
   * For page-level action buttons, use <TopBarAction> via Page's
   * `topBarAction` prop instead.
   */
  rightSlot?: ReactNode;
}

function renderTitle(title: string, accent?: string) {
  if (!accent) return title;
  const idx = title.lastIndexOf(accent);
  if (idx === -1) return title;
  return (
    <>
      {title.slice(0, idx)}
      <span className="font-display italic text-primary">
        {title.slice(idx, idx + accent.length)}
      </span>
      {title.slice(idx + accent.length)}
    </>
  );
}

export function PageHeader({ eyebrow, title, titleAccent, subtitle, rightSlot }: Props) {
  // Branded variant (eyebrow + italic accent) keeps the larger heading
  // for hero screens; grid screens get the compact single-row layout.
  const isBranded = Boolean(eyebrow || titleAccent);

  return (
    <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-6">
      <div className="min-w-0 flex-1">
        {eyebrow && (
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-primary">
            {eyebrow}
          </p>
        )}
        <h1
          className={
            isBranded
              ? 'text-[24px] font-semibold leading-[1.15] tracking-[-0.02em] text-foreground md:text-[32px] md:leading-[1.1]'
              : 'text-[20px] font-bold leading-tight tracking-tight text-foreground md:text-[22px]'
          }
        >
          {renderTitle(title, titleAccent)}
        </h1>
        {subtitle && (
          <p
            className={
              isBranded
                ? 'mt-1.5 text-[13.5px] leading-relaxed text-foreground-muted md:mt-2 md:text-[14.5px]'
                : 'mt-1 text-[13px] leading-relaxed text-foreground-muted'
            }
          >
            {subtitle}
          </p>
        )}
      </div>
      {rightSlot && (
        <div className="flex shrink-0 items-center gap-2 overflow-x-auto md:overflow-visible">
          {rightSlot}
        </div>
      )}
    </div>
  );
}
